import type { JSX } from "react";
import { ulid } from "ulid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, X } from "lucide-react";
import type { RecipeStep } from "@recipevault/shared";
import { Button } from "@/ui/button.js";

interface Props {
  value: RecipeStep[];
  onChange: (next: RecipeStep[]) => void;
}

function newStep(): RecipeStep {
  return { id: `step_${ulid()}`, text: "", kind: "active", timers: [], estimatedMin: null, parallelWith: [], imageKeys: [] };
}

function SortableStep({
  item,
  index,
  siblings,
  onChangeRow,
  onRemove,
}: {
  item: RecipeStep;
  index: number;
  siblings: { id: string; index: number }[];
  onChangeRow: (next: RecipeStep) => void;
  onRemove: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 bg-zinc-900 border border-zinc-800 rounded-md px-2 py-2"
    >
      <button
        type="button"
        className="text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing mt-1.5"
        aria-label="Reorder step"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <div className="text-zinc-500 text-xs mt-2 shrink-0 w-6 tabular-nums">{index + 1}.</div>
      <div className="flex-1 min-w-0 space-y-2">
        <textarea
          value={item.text}
          onChange={(e) => { onChangeRow({ ...item, text: e.target.value }); }}
          rows={Math.max(2, item.text.split("\n").length)}
          placeholder="Describe this step…"
          className="w-full min-h-[3rem] resize-y bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-700/40"
          aria-label={`Step ${index + 1} text`}
        />
        <div className="flex flex-wrap items-center gap-2">
          {(["active", "prep", "wait"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => { onChangeRow({ ...item, kind: k }); }}
              aria-label={`Mark step ${index + 1} as ${k}`}
              aria-pressed={item.kind === k}
              className={
                "px-2.5 py-1 rounded-full text-xs font-medium border " +
                (item.kind === k ? "bg-emerald-500 text-emerald-950 border-emerald-500" : "bg-zinc-900 text-zinc-300 border-zinc-700")
              }
            >
              {k === "active" ? "Active" : k === "prep" ? "Prep" : "Wait"}
            </button>
          ))}
          <label className="flex items-center gap-1 text-xs text-zinc-400">
            ~min
            <input
              type="number" min={1} max={10080}
              value={item.estimatedMin ?? ""}
              onChange={(e) => {
                const n = e.target.value === "" ? null : Number(e.target.value);
                onChangeRow({ ...item, estimatedMin: n != null && Number.isFinite(n) && n > 0 ? Math.floor(n) : null });
              }}
              aria-label={`Step ${index + 1} estimated minutes`}
              className="w-16 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-100"
            />
          </label>
        </div>
        <div className="space-y-1.5">
          {item.timers.map((t, ti) => (
            <div key={ti} className="flex items-center gap-2">
              <input
                value={t.label}
                onChange={(e) => { const timers = item.timers.map((x, j) => (j === ti ? { ...x, label: e.target.value.slice(0, 40) } : x)); onChangeRow({ ...item, timers }); }}
                placeholder="Timer label"
                aria-label={`Step ${index + 1} timer ${ti + 1} label`}
                className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100"
              />
              <input
                type="number" min={1} max={10080} value={t.minutes}
                onChange={(e) => { const m = Math.max(1, Math.min(10080, Math.floor(Number(e.target.value) || 1))); const timers = item.timers.map((x, j) => (j === ti ? { ...x, minutes: m } : x)); onChangeRow({ ...item, timers }); }}
                aria-label={`Step ${index + 1} timer ${ti + 1} minutes`}
                className="w-16 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100"
              />
              <span className="text-xs text-zinc-500">min</span>
              <button type="button" onClick={() => { onChangeRow({ ...item, timers: item.timers.filter((_, j) => j !== ti) }); }} aria-label={`Remove step ${index + 1} timer ${ti + 1}`} className="text-zinc-500 hover:text-destructive p-1">
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ))}
          {item.timers.length < 20 && (
            <button type="button" onClick={() => { onChangeRow({ ...item, timers: [...item.timers, { label: "", minutes: 5 }] }); }} aria-label={`Add timer to step ${index + 1}`} className="text-xs text-emerald-400 hover:text-emerald-300">+ Add timer</button>
          )}
        </div>
        {siblings.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-zinc-500">Parallel with:</span>
            {siblings.map((sib) => {
              const on = item.parallelWith.includes(sib.id);
              return (
                <button key={sib.id} type="button"
                  onClick={() => { const parallelWith = on ? item.parallelWith.filter((id) => id !== sib.id) : [...item.parallelWith, sib.id]; onChangeRow({ ...item, parallelWith }); }}
                  aria-label={`Step ${index + 1} runs parallel with step ${sib.index + 1}`}
                  aria-pressed={on}
                  className={"px-2 py-0.5 rounded-full text-xs border " + (on ? "bg-amber-500 text-amber-950 border-amber-500" : "bg-zinc-900 text-zinc-400 border-zinc-700")}
                >{sib.index + 1}</button>
              );
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-zinc-500 hover:text-destructive p-1 mt-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
        aria-label="Remove step"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export function StepList({ value, onChange }: Props): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = value.findIndex((i) => i.id === active.id);
    const to = value.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(value, from, to));
  }

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {value.map((item, idx) => (
            <SortableStep
              key={item.id}
              item={item}
              index={idx}
              siblings={value.map((v, i) => ({ id: v.id, index: i })).filter((s) => s.id !== item.id)}
              onChangeRow={(next) => { onChange(value.map((v) => (v.id === item.id ? next : v))); }}
              onRemove={() => { onChange(value.filter((v) => v.id !== item.id)); }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => { onChange([...value, newStep()]); }}
      >
        <Plus className="mr-2 h-4 w-4" aria-hidden />
        Add step
      </Button>
    </div>
  );
}
